import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Building2, Mail, Lock, User, Phone } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import toast from 'react-hot-toast';

const RegisterPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const defaultRole = searchParams.get('role') || 'ROLE_LOCATAIRE';
  
  const [formData, setFormData] = useState({
    email: '', password: '', confirmPassword: '',
    nom: '', prenom: '', telephone: '',
    role: defaultRole as 'ROLE_GERANT' | 'ROLE_LOCATAIRE'
  });
  const [showPassword, setShowPassword] = useState(false);
  const { register, isLoading, error, clearError } = useAuthStore();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    if (formData.password !== formData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (formData.password.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    const success = await register({
      email: formData.email, password: formData.password,
      nom: formData.nom, prenom: formData.prenom,
      telephone: formData.telephone || undefined, role: formData.role
    });
    if (success) {
      toast.success('Inscription réussie ! Vérifiez votre email.');
      navigate('/login');
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center space-x-2 mb-6">
            <div className="w-12 h-12 bg-primary-500 rounded-xl flex items-center justify-center">
              <Building2 className="w-7 h-7 text-white" />
            </div>
          </Link>
          <h1 className="text-3xl font-heading font-bold text-neutral-900">Inscription</h1>
          <p className="mt-2 text-neutral-600">Créez votre compte {formData.role === 'ROLE_GERANT' ? 'gérant' : 'locataire'}</p>
        </div>

        <div className="card p-8">
          {/* Role Toggle */}
          <div className="flex mb-6 p-1 bg-neutral-100 rounded-lg">
            <button type="button" onClick={() => setFormData({...formData, role: 'ROLE_LOCATAIRE'})}
              className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${formData.role === 'ROLE_LOCATAIRE' ? 'bg-white shadow text-primary-600' : 'text-neutral-600'}`}>
              Locataire
            </button>
            <button type="button" onClick={() => setFormData({...formData, role: 'ROLE_GERANT'})}
              className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${formData.role === 'ROLE_GERANT' ? 'bg-white shadow text-primary-600' : 'text-neutral-600'}`}>
              Gérant
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Prénom</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                  <input type="text" value={formData.prenom} onChange={(e) => setFormData({...formData, prenom: e.target.value})}
                    className="input pl-10" placeholder="Jean" required />
                </div>
              </div>
              <div>
                <label className="label">Nom</label>
                <input type="text" value={formData.nom} onChange={(e) => setFormData({...formData, nom: e.target.value})}
                  className="input" placeholder="Dupont" required />
              </div>
            </div>

            <div>
              <label className="label">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="input pl-10" placeholder="votre@email.com" required />
              </div>
            </div>

            <div>
              <label className="label">Téléphone (optionnel)</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                <input type="tel" value={formData.telephone} onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                  className="input pl-10" placeholder="+221 77 123 45 67" />
              </div>
            </div>

            <div>
              <label className="label">Mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                <input type={showPassword ? 'text' : 'password'} value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="input pl-10 pr-10" placeholder="••••••••" required />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600">
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="label">Confirmer le mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                <input type={showPassword ? 'text' : 'password'} value={formData.confirmPassword}
                  onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                  className="input pl-10" placeholder="••••••••" required />
              </div>
            </div>

            <button type="submit" disabled={isLoading} className="w-full btn btn-primary btn-lg mt-6">
              {isLoading ? 'Inscription...' : 'S\'inscrire'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-neutral-600">
              Déjà un compte ? <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">Se connecter</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
